/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable max-len */
import { Typography } from '@mui/material';
import { NavigationTarget } from '../common/utils/useNavigationUtils';

export interface DashboardQuestion {
  name: string;
  summary: string;
  Detail: () => JSX.Element;
}

export const generateDashboardQuestions = (
  solutionOrg: string,
  openExternalLink: (url: string, navigationTarget?: NavigationTarget) => void,
  showAllPartners = false,
): DashboardQuestion[] => [
  {
    name: 'panel1',
    summary: 'How do I earn rewards?',
    Detail: () => (
      <>
        <Typography>
            To start earning rewards, first make sure your account with participating rewards partners (e.g., The Home Depot, Sunbelt Rentals) is connected to Edge. Follow the instructions specific to each partner to confirm your account details.
          <br />
            Once you enroll with one of our partnered suppliers, they'll track your eligible purchases throughout the rewards period. We'll handle the rest, making sure you receive your rewards.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Connecting Your Home Depot Account</h4>
            To connect your Home Depot Pro Xtra account to Edge, you'll need to complete the process through the Edge platform. Follow these steps:
          <ol>
            <li>Go to the Partners page of your Edge account and find the Home Depot section.</li>
            <li>Complete the required steps, including registering your credit card with Home Depot Pro Xtra and entering the code "JOIST".</li>
            <li>Return to Edge and enter your Home Depot Pro Xtra account email address to complete your enrollment.</li>
          </ol>
            Be sure to use the credit card registered to your Home Depot Pro Xtra account for all purchases to earn your rewards.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Connecting Your HD Supply Account</h4>
            If you don't have an HD Supply account, sign up using our online registration form.
          <br />
            To connect an existing HD Supply account to Edge:
          <ol>
            <li>Locate your HD Supply account number.</li>
            <li>Go to the Partners page in your Edge account and scroll down to the HD Supply section.</li>
            <li>Follow the prompts to enter your account number and complete the enrollment process.</li>
            <li>You'll receive a confirmation email once your account is successfully connected.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Capital Wraps</h4>
          <ol>
            <li>Go to the Partners page in your Edge account and scroll down to Capital Wraps.</li>
            <li>Click to open the Capital Wraps enrollment form and complete the required fields.</li>
            <li>After submitting the form, a Capital Wraps representative will contact you soon to finalize your enrollment.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Spartan Tool</h4>
          <ol>
            <li>Go to the Partners page in your Edge account and scroll down to Spartan Tool.</li>
            <li>Enter the specific Spartan Tool enrollment code.</li>
            <li>Follow the prompts to complete the enrollment process.</li>
            <li>Your purchases will now be tracked for reward eligibility.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Winsupply</h4>
          <ol>
            <li>Go to the Partners page in your Edge account and scroll down to Winsupply.</li>
            <li>Complete the enrollment form. You'll receive an email with next steps.</li>
            <li><a href='#' target="_blank" rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openExternalLink('https://www.winsupplyinc.com/ftue');
              }}
            >Contact the Winsupply branch</a> you'd like to buy from. They'll guide you through a quick credit application and issue your Winsupply account number.</li>
            <li>Use that account number for purchases at your branch—every qualifying purchase will now earn Edge rewards.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With WeSupplyTrades</h4>
          <ol>
            <li>Locate WeSupplyTrades on the <a href='/partners' rel="noopener noreferrer">Partners page</a>.</li>
            <li>Complete and submit the enrollment form. </li>
            <li>Check your inbox for an email from WeSupplyTrades; follow the instructions inside to activate your PRO account and begin placing orders. </li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Connecting Your Coast Account</h4>
          <ol>
            <li>On Edge, go to the Partners page and add Coast as a reward partner. </li>
            <li>Follow the link to Coast's application page to complete the credit application online. </li>
            <li>Once approved, you will receive your Coast card in the mail. </li>
            <li>Start using your Coast card at participating fuel stations. Your savings will appear as a statement credit 4-6 weeks after each month's end.  </li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Sunbelt</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and find the Sunbelt Rentals section.</li>
            <li>Click Enroll to open the Sunbelt Rentals sign-up form.</li>
            <li>Complete the form using your business and rental account details, then submit.</li>
            <li>You'll receive a unique link via email to finalize your Sunbelt Rentals account setup. Follow that link to complete the process and enable rewards tracking.</li>
            <li>Once your account is linked, all qualifying Sunbelt Rentals transactions will automatically earn Edge rewards.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With EverConnect</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and navigate to EverConnect.</li>
            <li>Click to open the EverConnect enrollment form and complete the required fields.</li>
            <li>After submitting the form, an EverConnect representative will contact you soon to finalize your enrollment. </li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Rinnai</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and find Rinnai.</li>
            <li>Open the Rinnai enrollment form and fill in the required business details.</li>
            <li>Select HD Supply or Winsupply as your distributor (where you'll buy Rinnai products), then click Complete Enrollment.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With PostcardMania</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and click PostcardMania.</li>
            <li>Click "+ Add rewards" to open the PostcardMania enrollment form.</li>
            <li>Fill out the form.</li>
            <li>A PostcardMania expert will reach out to help you place your order and apply your 10% discount. </li>
            <li>Future orders can be placed directly through your PostcardMania customer portal. </li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Baytek</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and click Baytek.</li>
            <li>Click "+ Add rewards" to open the Baytek enrollment form.</li>
            <li>Fill out the form.</li>
            <li>A Baytek specialist will reach out to schedule your free consultation.</li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Behr</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and click Behr.</li>
            <li>Click "+ Add rewards" to open the Behr enrollment form.</li>
            <li>Fill out the form.</li>
            <li>Make sure you are also enrolled with Home Depot (where you'll buy Behr products), then click Complete Enrollment. </li>
          </ol>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Enrolling With Gusto</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and click Gusto.</li>
            <li>Click "+ Add rewards" to open the Gusto enrollment form.</li>
            <li>Fill out the form.</li>
            <li>You'll receive an email with next steps to finalize your Gusto account setup.</li>
          </ol>
        </Typography>
        <Typography component="div">
          <h4 style={{ marginBottom: '5px' }}>Enrolling With HDX</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and click HDX.</li>
            <li>Click "+ Add rewards" to open the HDX enrollment form.</li>
            <li>Fill out the form.</li>
            <li>Make sure you are also enrolled with Home Depot (where you'll buy HDX products), then click Complete Enrollment.</li>
          </ol>
        </Typography>
        <Typography component="div">
          <h4 style={{ marginBottom: '5px' }}>Enrolling With NOVO Professional Water Systems</h4>
          <ol>
            <li>Go to the <a href='/partners' rel="noopener noreferrer">Partners page</a> in your Edge account and click NOVO Professional Water Systems.</li>
            <li>Click "+ Add rewards" to open the enrollment form.</li>
            <li>Fill out the form.</li>
            <li>Select HD Supply or Winsupply as your distributor (where you'll buy NOVO Professional Water Systems products), then click Complete Enrollment.</li>
          </ol>
        </Typography>
      </>),
  },
  {
    name: 'panel2',
    summary: 'How much can I earn?',
    Detail: () => (
      <Typography>
          Your rewards are based on the purchases you make through connected partner accounts. The more you spend on qualifying purchases, the more you earn. Specific reward percentages or cash-back amounts depend on the partner and their program terms.
      </Typography>
    ),
  },
  {
    name: 'panel3',
    summary: 'Can I participate in multiple rewards programs?  ',
    Detail: () => (
      <>
        <Typography>
            Reward partners typically allow participation in only one rewards program at a time. If you are already enrolled in a partner's rewards program, you can switch to earning rewards through Edge by linking your existing program to Edge. To do this, you must inform the supplier in writing of your decision to switch and submit the necessary information through the Edge platform.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>The Home Depot</h4>
            If you're already enrolled in Home Depot's Pro Xtra program, you can earn additional rewards through Edge on eligible purchases. The rewards you earn through Edge are in addition to the rewards you already receive as a Pro Xtra account member, allowing you to maximize your benefits.
          <br />
            However, Home Depot allows enrollment in only one national account rewards program at a time. If you're already enrolled in another national rewards program through an association or similar organization, you must choose between that program and Edge.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>HD Supply</h4>
            HD Supply allows enrollment in only one company rewards program at a time. This means you cannot stack Edge rewards with other rewards programs offered by HD Supply. If you're already enrolled in another HD Supply rewards program, you would need to switch to Edge to earn rewards through the platform.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Capital Wraps</h4>
            Capital Wraps allows enrollment in only one rewards program.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Spartan Tool </h4>
            Spartan Tool permits enrollment in only one rewards program at a time. If you haven't chosen a different program, your purchases will default to earning rewards through Edge once you link your Spartan Tool account.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Winsupply</h4>
            Winsupply allows enrollment in only one reward or buying‑group incentive at a time. Edge users already in another Winsupply reward/buying group program would need to switch to Edge to earn Winsupply rewards here.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>WeSupplyTrades </h4>
            WeSupplyTrades allows enrollment in only one rewards program at a time. Edge is treated as the default program unless you notify the supplier in writing that you prefer a different group.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Coast </h4>
            Coast allows enrollment in multiple rewards programs, so you can participate in Edge and other partner fuel programs (like ExxonMobil, CircleK, or 7-Eleven) at the same time. Just make sure to apply through the Coast link provided by Edge so your rewards are tracked correctly.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Sunbelt Rentals</h4>
            Sunbelt allows enrollment in only one rewards program at a time. Edge is treated as your default enrollment unless you notify Sunbelt in writing that you prefer a different program.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>EverConnect</h4>
            Only one offer per customer. Offers may not be combined.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Rinnai</h4>
            You can only participate in one Rinnai rewards program at a time. However, Rinnai rewards do stack with Winsupply or HD Supply rewards.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>PostcardMania</h4>
            You cannot participate in more than one PostcardMania rewards program. Edge is treated as the default program unless you notify PostcardMania otherwise.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Baytek </h4>
            You cannot participate in more than one Baytek rewards program. Edge is treated as the default program unless you notify Baytek otherwise.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Behr</h4>
            You cannot participate in more than one Behr rewards program. Edge is treated as the default program unless you notify Behr otherwise.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Gusto</h4>
            You cannot participate in more than one Gusto rewards program. Edge is treated as the default program unless you notify Gusto otherwise.
        </Typography>
        <Typography component="div">
          <h4 style={{ marginBottom: '5px' }}>HDX</h4>
            You cannot participate in more than one HDX rewards program. Edge is treated as the default program unless you notify HDX otherwise.
        </Typography>
        <Typography component="div">
          <h4 style={{ marginBottom: '5px' }}>NOVO Professional Water Systems</h4>
            You cannot participate in more than one NOVO Professional Water Systems rewards program. Edge is treated as the default program unless you notify NOVO Professional Water Systems otherwise.
        </Typography>
        {showAllPartners && <>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>Lennox</h4>
              Lennox allows enrollment in only one rewards program. Customers can request, in writing, to transfer their rebates to Service Fusion Edge.
          </Typography>
        </>}
      </>
    ),
  },
  {
    name: 'panel4',
    summary: 'What purchases count toward rewards?',
    Detail: () => (
      <>
        <Typography>
            Rewards apply to qualifying purchases made through participating partners. While most purchases are eligible, exceptions may vary by partner. For example:
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Home Depot Exceptions</h4>
            Exclusions may include sales tax, shipping costs, and certain items.
          <br />
            Pro Tip: To receive rewards, always make purchases using the credit card registered to your Home Depot Pro Xtra account.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>HD Supply Exceptions</h4>
            Exclusions include Perma-Liner equipment, Generac home standby generators, and some other items.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Capital Wraps Exceptions</h4>
            Business cards and custom illustrations are excluded from the rewards program. Sales tax and delivery charges do not count toward rewards.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Spartan Tool Exceptions</h4>
            Sales tax and delivery charges are excluded from reward eligibility.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Winsupply Exceptions</h4>
          <ul>
            <li>Sales tax, freight, services, delivery charges, and returns do not count toward reward totals.</li>
            <li>Returns will be deducted from future rewards at Winsupply's discretion.</li>
          </ul>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>WeSupplyTrades Exceptions</h4>
          <ul>
            <li>Sales tax and delivery charges do not earn rewards.</li>
            <li>WeSupplyTrades ship within the Continental United States, excluding California.</li>
          </ul>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Coast Exceptions</h4>
            Sales tax, chargebacks, returns, refunds, fees and other non-purchase charges (e.g., late fees, service fees) do not count toward statement credits or bonus offers.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Sunbelt Rentals Exceptions</h4>
            Applicable taxes, freight charges, fuel charges, environmental fees, Rental Protection Plans, or any other applicable ancillary charges do not qualify for rewards. Customers are only eligible for rewards if each invoice is paid within its terms.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>EverConnect Exceptions</h4>
            The $750 promotional credit can only be used on EverConnect, Best Pick Reports, Five Star Rated, and Socius Marketing. Rewards apply only to purchases that meet eligibility requirements (e.g., first-time customers, service upgrades, or returning customers inactive for 180+ days). This offer has no cash value and cannot be combined with other promotions.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Rinnai Exceptions</h4>
            Only certain Rinnai products are eligible for rewards. Check the <a href='https://d2o7bp9oz3gksv.cloudfront.net/Rinnai_Qualifying_Products_c385409785.pdf' rel="noopener noreferrer">full list of qualifying products</a> before you buy.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>PostcardMania Exceptions</h4>
            Sales tax and shipping charges do not earn rewards.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Baytek Exceptions</h4>
            Applies only to new customers. To qualify, the trial must remain active for at least 60 days.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Behr Exceptions</h4>
            Applies only to Behr paint.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Gusto Exceptions</h4>
            None.
        </Typography>
        <Typography component="div">
          <h4 style={{ marginBottom: '5px' }}>HDX Exceptions</h4>
            Sales tax and delivery charges do not earn rewards.
        </Typography>
        <Typography component="div">
          <h4 style={{ marginBottom: '5px' }}>NOVO Professional Water Systems Exceptions</h4>
            None.
        </Typography>
        {showAllPartners && <>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>Lennox Exceptions</h4>
              Exclusions include commodities (copper, refrigerant, certain duct), residential new construction (RNC) serialized equipment, commercial equipment, warranty product, equipment purchased through retailers, and equipment purchased through property management channels (e.g., American Homes 4 Rent, Invitation Homes). Customers part of a national account or another best practices group, or with an existing Lennox agreement, are ineligible.
          </Typography>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>PlumbMaster Exceptions</h4>
              Rebate is based on combined purchases from PlumbMaster, Wolverine Brass, and Speakman.
          </Typography>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>WinSupply Exceptions</h4>
              No product exclusions. All purchases earn rebates.
            <br />
              Local stores control customer rebate eligibility. Customers with favorable front-end discounts or pricing can be classified as ineligible for rebates or eligible for growth-only rebates.
            <br />
              Customer's account must be in good standing to earn rebates. Rebates are forfeited if the account is past due.
          </Typography>
        </>}
      </>
    ),
  },
  {
    name: 'panel5',
    summary: 'Is there a minimum purchase required to qualify for rewards?',
    Detail: () => (
      <>
        <Typography>
            It depends. Some partners require a minimum spend to earn rewards. Check each partner's details below:
        </Typography>
        <Typography>
          <ul>
            <li>
              <h4 style={{ marginBottom: '5px' }}>Home Depot:</h4>Requires a minimum spend of $12,500 per 6-month period.
            </li>
            <li>
              <h4 style={{ marginBottom: '5px' }}>Behr:</h4>Requires a minimum spend of $6,500 per 6-month period.
            </li>
            <li>
              <h4 style={{ marginBottom: '5px' }}>Coast:</h4>No minimum spend required to earn the standard Coast fuel discount (0.5% back as a statement credit). But to earn the $250 Coast sign-up bonus, you must spend $2,500 within the first 3 months of being approved.
            </li>
            <li>
              <h4 style={{ marginBottom: '5px' }}>HD Supply, Capital Wraps, Spartan Tool, Sunbelt Rentals, Winsupply, EverConnect, Rinnai, WeSupplyTrades, Baytek, Gusto, HDX, and NOVO Professional Water Systems:</h4>No minimum spend requirement.
            </li>
            <li>
              <h4 style={{ marginBottom: '5px' }}>PostcardMania:</h4>The minimum PostcardMania order is 1,000 mailers, so plan your first campaign with that in mind.
            </li>
          </ul>
        </Typography>
        {showAllPartners && <>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>Lennox Minimum Purchase Requirements</h4>
              Customer purchases must exceed $25,000 annually, adjusted for quarterly seasonality. Breakdown:
            <ul>
              <li>$7,500 per quarter in Q2 & Q3 (high season)</li>
              <li>$5,000 per quarter in Q1 & Q4 (low season)</li>
            </ul>
              Rebates begin the quarter the customer's Lennox dealer number is recorded on their Service Fusion profile.
          </Typography>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>PlumbMaster Minimum Purchase Requirements</h4>
              No minimum spend requirement.
          </Typography>
          <Typography>
            <h4 style={{ marginBottom: '5px' }}>WinSupply Minimum Purchase Requirements</h4>
              No minimum spend requirement.
          </Typography>
        </>}
      </>
    ),
  },
  {
    name: 'panel6',
    summary: 'When will I receive my rewards?',
    Detail: () => (
      <>
        <Typography>
            Rewards payout schedules depend on the partner. See details below. We strive to process payouts promptly, but the exact timing may vary.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Annual Payout Schedule</h4>
            Sunbelt Rentals: Rewards are calculated annually and paid after the end of each calendar year.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Semi-annual Payout Schedule</h4>
            The Home Depot, Behr, and HDX provide payouts semi-annually.
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Quarterly Payout Schedule</h4>
            The partners below issue rewards quarterly. Any purchases made in one quarter will have their rewards paid out in the next.
          <br />
            For example, rewards for purchases made in Q1 (January-March) will be paid in Q2 (April-June).
          <ul>
            <li>HD Supply</li>
            <li>Capital Wraps</li>
            <li>Spartan Tool</li>
            <li>Winsupply</li>
            <li>WeSupplyTrades</li>
            <li>Rinnai</li>
            <li>PostcardMania</li>
            <li>Baytek</li>
            <li>NOVO Professional Water Systems</li>
            {showAllPartners && <>
              <li>Lennox</li>
              <li>PlumbMaster</li>
            </>}
          </ul>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>Monthly Payout Schedule</h4>
          <ul>
            <li>Coast:</li>
            <ul>
              <li>Coast issues rewards as monthly statement credits. These appear on your Coast account 4-6 weeks after the end of each calendar month.</li>
              <li>Sign-up bonuses (if qualified) are also credited 4–6 weeks after reaching the minimum purchase point.</li>
            </ul>
            <li>Gusto:</li>
            <ul>
              <li>Gusto issues rewards on a monthly schedule and will be paid the following month.</li>
            </ul>
          </ul>
        </Typography>
        <Typography>
          <h4 style={{ marginBottom: '5px' }}>One-Time Promotional Credit</h4>
            EverConnect: The $750 promotional credit will be applied to your second month's billing statement, after your first month's bill has been paid in full.
        </Typography>
      </>
    ),
  },
  {
    name: 'panel7',
    summary: 'Where will I receive my rewards?',
    Detail: () => (
      <>
        <Typography>
            Your payout goes to the method you selected during onboarding:
          <ul>
            <li><strong>Direct Deposit (ACH)</strong> – rewards are automatically transferred to the business bank account you added.</li>
            <li><strong>PayPal Business</strong> – rewards are sent to the verified PayPal email linked to your account. </li>
          </ul>
          <br />
            Need to double‑check or update your choice?
          <br />
            Head to <strong>Wallet → Payout Settings → Manage</strong> and make any changes there. If you'd like to use PayPal but don't yet have an account, you can set one up for free at <a href='#' target="_blank" rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openExternalLink('https://www.paypal.com/business/getting-started');
            }}>
              https://www.paypal.com/business/getting-started
          </a>
        </Typography>
      </>
    ),
  },
];
